/**
 * useCalendarPermissions — calendar-scoped permission predicates.
 *
 * Returns memoized predicates the Calendar / Appointments UI uses to
 * decide whether the current user can edit or resize an appointment.
 *
 * Why a hook (not utils): the predicates close over `user` from
 * `useAuth()` and `users` from `useStaticData()` so we'd otherwise
 * have to either (a) pass both as arguments to every call site or
 * (b) leave them as Dashboard-scope closures. A hook is the right
 * compromise.
 */

import { useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useStaticData } from '../context/StaticDataContext';
import { isSuperAdmin } from '../utils/helpers';

export const useCalendarPermissions = () => {
  const { user } = useAuth();
  const { users } = useStaticData();

  // Super-admin or legacy `admin` role.
  const isAdmin = useCallback(() => {
    if (!user) return false;
    return isSuperAdmin(user);
  }, [user]);

  // For now every appointment can be resized — the duration is
  // validated server-side.  This wrapper stays as a hook so we can
  // tighten the rule (e.g., locked notes, completed claims) without
  // touching every call site.
  const canResizeAppointment = useCallback(() => true, []);

  /**
   * Whether `user` can edit a given appointment.
   *
   * Resolution order:
   *   1. Admin / Super-Admin → yes.
   *   2. Office role → yes (they own scheduling).
   *   3. The appointment's provider → yes.
   *   4. A supervisor whose supervisee is the appointment's provider
   *      (checked both via the static `supervisor_ids` array on the
   *      provider record AND via dynamic role flags).
   *   5. Otherwise → no.
   */
  const canEditAppointment = useCallback(
    (appointment) => {
      if (!appointment || !user) return false;

      const role = (user.role || '').toLowerCase();
      if (role === 'admin' || isSuperAdmin(user)) return true;
      if (role === 'office') return true;
      if (appointment.provider_id === user.id) return true;

      const appointmentProvider = users?.find(
        (u) => u.id === appointment.provider_id
      );
      if (appointmentProvider?.supervisor_ids?.includes(user.id)) return true;

      if (user.is_supervisor && users?.length) {
        const superviseeIds = users
          .filter((u) => u.supervisor_ids?.includes(user.id))
          .map((u) => u.id);
        if (superviseeIds.includes(appointment.provider_id)) return true;
      }

      return false;
    },
    [user, users]
  );

  return useMemo(
    () => ({ isAdmin, canEditAppointment, canResizeAppointment }),
    [isAdmin, canEditAppointment, canResizeAppointment]
  );
};

export default useCalendarPermissions;
